import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { apiFetch } from '../lib/api';
import { ENDPOINTS } from '../constants/endpoints';
import type { TestStatus } from '../constants/test-status';
import type { DateRange } from '../helpers/get-current-test-period-range';

export interface TestReportMember {
  id: number;
  name: string;
  surname: string;
  averageScore: number | null;
  testsCount: number;
}

export interface TestReportResponse {
  members: TestReportMember[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface TestReportFilters {
  startDate: string;
  endDate: string;
  departmentIds?: number[];
}

export interface MemberTest {
  id: number;
  testNumber: number;
  startedAt: string;
  endedAt: string;
  status: TestStatus;
  score: number;
  isGenerated: boolean;
}

export interface MemberTestsResponse {
  tests: MemberTest[];
  total: number;
}

async function fetchOrganizationTests(
  organizationId: number,
  page: number,
  limit: number,
  filters: TestReportFilters,
): Promise<TestReportResponse> {
  const response = await apiFetch(
    ENDPOINTS.ORGANIZATION_STATS_TESTS(
      organizationId,
      page,
      limit,
      filters.startDate,
      filters.endDate,
      filters.departmentIds,
    ),
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to fetch tests');
  }

  return response.json();
}

export function useOrganizationTests(
  organizationId: number | undefined,
  page: number,
  limit: number,
  filters: TestReportFilters,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: ['organization', organizationId, 'tests', page, limit, filters],
    queryFn: () =>
      fetchOrganizationTests(organizationId!, page, limit, filters),
    enabled: !!organizationId && enabled,
    placeholderData: keepPreviousData,
  });
}

async function fetchMemberTests(
  organizationId: number,
  userId: number,
  range: DateRange,
): Promise<MemberTestsResponse> {
  const response = await apiFetch(
    ENDPOINTS.ORGANIZATION_STATS_MEMBER_TESTS(
      organizationId,
      userId,
      range.startDate,
      range.endDate,
    ),
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to fetch member tests');
  }

  return response.json();
}

export function useMemberTests(
  organizationId: number | undefined,
  userId: number | undefined,
  range: DateRange,
  enabled: boolean = false,
) {
  return useQuery({
    queryKey: ['organization', organizationId, 'tests', userId, range],
    queryFn: () => fetchMemberTests(organizationId!, userId!, range),
    enabled: !!organizationId && !!userId && enabled,
  });
}
